<template>
  <div class="rt-shell">
    <!-- ===== Thanh trên ===== -->
    <div class="rt-topbar">
      <div class="rt-brand"><span class="rt-brand-dot"></span> Theo dõi thông báo hoạt động</div>

      <div class="rt-datenav">
        <button class="rt-iconbtn" aria-label="Ngày trước" @click="shiftDay(-1)">‹</button>
        <span class="rt-datelabel">{{ dateLabel }}</span>
        <button class="rt-iconbtn" aria-label="Ngày sau" @click="shiftDay(1)">›</button>
      </div>

      <div class="rt-seg">
        <button
          v-for="r in RANGES"
          :key="r.days"
          :class="{ 'rt-seg-on': rangeDays === r.days }"
          @click="setRange(r.days)"
        >
          {{ r.text }}
        </button>
      </div>

      <div class="rt-topright">
        <span class="rt-clock"><i class="rt-pulse"></i> {{ clockText }}</span>

        <button class="rt-btn" :disabled="loading" @click="loadData">
          {{ loading ? 'Đang tải…' : 'Làm mới' }}
        </button>
      </div>
    </div>

    <!-- ===== Thân ===== -->
    <div class="rt-body">
      <aside class="rt-side">
        <div v-if="lockUserPackageId" class="rt-lock">
          <div class="rt-lock-head">Đang xem một gói</div>
          <div class="rt-lock-name" :title="lockLabel">
            {{ lockLabel || 'Gói dịch vụ' }}
          </div>
          <button class="rt-clear" @click="clearLock">Xem tất cả gói</button>
        </div>

        <div class="rt-field">
          <span class="rt-flabel">Tìm kiếm</span>
          <b-form-input
            v-model="search"
            class="rt-input"
            type="text"
            placeholder="Mã RMD hoặc tên khách hàng"
          />
        </div>

        <!-- Đặt TRÊN thanh trạng thái vì nó cắt tập dữ liệu mà thanh kia đếm: bỏ tick
             xong thấy mọi con số nhảy là đúng, chứ không phải lỗi. -->
        <div class="rt-field">
          <span class="rt-flabel">Chế độ gói</span>
          <b-form-checkbox v-model="modes" value="1" class="rt-check" :disabled="isOnlyMode('1')">
            AI Mode
          </b-form-checkbox>
          <b-form-checkbox v-model="modes" value="0" class="rt-check" :disabled="isOnlyMode('0')">
            Normal
          </b-form-checkbox>
        </div>

        <div class="rt-field">
          <div class="rt-flabel-row">
            <span class="rt-flabel">Trạng thái</span>
            <button v-if="activeState !== null" class="rt-clear" @click="activeState = null">
              Bỏ lọc
            </button>
          </div>

          <button
            v-for="st in stateChips"
            :key="st.key"
            class="rt-stitem"
            :class="[`rt-s-${st.key}`, { 'rt-stitem-on': activeState === st.value }]"
            @click="toggleState(st.value)"
          >
            <i class="rt-swatch"></i>
            <span class="rt-stlabel">{{ st.label }}</span>
            <b class="rt-num">{{ st.count }}</b>
          </button>
        </div>

        <div class="rt-field">
          <span class="rt-flabel">Kênh gửi</span>
          <b-form-checkbox v-model="methods" value="1" class="rt-check">Zalo</b-form-checkbox>
          <b-form-checkbox v-model="methods" value="2" class="rt-check">App</b-form-checkbox>
        </div>

        <div class="rt-field">
          <span class="rt-flabel">Coach</span>
          <b-dropdown size="sm" variant="outline-secondary" block :text="coachFilterLabel" menu-class="rt-dd-menu">
            <b-dropdown-item :active="coachFilter === 'all'" @click="coachFilter = 'all'">
              Tất cả ({{ coachOptions.length }})
            </b-dropdown-item>
            <b-dropdown-item
              v-for="c in coachOptions"
              :key="c.id"
              :active="coachFilter === c.id"
              @click="coachFilter = c.id"
            >
              {{ c.name }}
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <div class="rt-field">
          <span class="rt-flabel">Khách hàng</span>
          <b-dropdown
            size="sm"
            variant="outline-secondary"
            block
            :text="accountFilterLabel"
            :disabled="!customerOptions.length"
            menu-class="rt-dd-menu"
          >
            <b-dropdown-item :active="accountFilter === 'all'" @click="accountFilter = 'all'">
              Tất cả ({{ customerOptions.length }})
            </b-dropdown-item>
            <b-dropdown-item
              v-for="c in customerOptions"
              :key="c.id"
              :active="accountFilter === c.id"
              @click="accountFilter = c.id"
            >
              {{ c.name }}
            </b-dropdown-item>
          </b-dropdown>
          <p v-if="coachFilter !== 'all'" class="rt-hint">
            Chỉ liệt kê khách của coach đang chọn.
          </p>
        </div>

        <div class="rt-sum">
          <div class="rt-sum-lbl">Đang hiển thị</div>
          <div class="rt-sum-big">{{ visibleItems.length }} tin</div>
          <div class="rt-sum-lbl">Khách hàng</div>
          <div class="rt-sum-big">{{ customerCount }} khách hàng</div>
        </div>

        <p class="rt-note">
          Khoảng xem tối đa 31 ngày. Mọi giờ hiển thị theo giờ Việt Nam (GMT+7).
        </p>
      </aside>

      <div class="rt-main">
        <div class="rt-mainhead">
          <div>
            <h2 class="rt-h2">Danh sách nhắc nhở hoạt động chương trình</h2>
            <div class="rt-sub">{{ rangeSub }}</div>
          </div>
          <div class="rt-sortbar">
            <!-- Segmented control chứ không phải hai nút xanh đặc: đây là chọn CHẾ ĐỘ XEM,
                 không phải hành động. Nút xanh đặc ngang hàng với "Làm mới" làm người dùng
                 tưởng bấm vào sẽ có chuyện gì đó xảy ra. -->
            <div class="rt-seg rt-seg-sm">
              <button :class="{ 'rt-seg-on': grouped }" @click="grouped = true">
                Nhóm theo khách
              </button>
              <button :class="{ 'rt-seg-on': !grouped }" @click="grouped = false">
                Danh sách
              </button>
            </div>

            <button v-if="grouped" class="rt-ghost" @click="toggleAllGroups">
              {{ allGroupsOpen ? 'Thu gọn tất cả' : 'Mở tất cả' }}
            </button>

            <span class="rt-divider"></span>

            <b-dropdown size="sm" variant="outline-secondary" right :text="sortModeLabel">
              <b-dropdown-item
                v-for="opt in sortModeOptions"
                :key="opt.value"
                :active="sortMode === opt.value"
                @click="sortMode = opt.value"
              >
                {{ opt.text }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>

        <div class="rt-tablebox">
          <table class="rt-table">
            <!-- Hai chế độ có cột khác nhau nên phải có hai tiêu đề. Dùng chung một
                 tiêu đề thì ở chế độ nhóm, nhãn cột không khớp dữ liệu bên dưới. -->
            <thead v-if="grouped">
              <tr>
                <th class="rt-w-time">Giờ gửi</th>
                <th class="rt-w-cust">Hoạt động</th>
                <th class="rt-w-chan">Kênh</th>
                <th colspan="2">Nội dung</th>
                <th class="rt-w-state">Trạng thái</th>
                <th class="rt-w-act">Thao tác</th>
              </tr>
            </thead>

            <thead v-else>
              <tr>
                <th class="rt-w-cust">Khách hàng</th>
                <!-- Chủ đề thay cho Coach: bộ lọc bên trái đã chọn coach rồi, còn chủ đề
                     là thứ phân biệt được các dòng trong cùng một khách. -->
                <th class="rt-w-topic">Chủ đề</th>
                <th class="rt-w-chan">Kênh</th>
                <th>Nội dung</th>
                <th class="rt-w-time">Giờ gửi</th>
                <th class="rt-w-state">Trạng thái</th>
                <th class="rt-w-act">Thao tác</th>
              </tr>
            </thead>
            <!-- Nhóm 2 cấp: khách -> chủ đề. 1400 dòng phẳng thì không ai đọc được;
                 gộp lại thì mở đúng khách đang cần rồi mới xuống tới từng giờ gửi. -->
            <tbody v-if="grouped">
              <template v-for="g in pagedGroups">
                <tr :key="g.key" class="rt-gcust" @click="toggleGroup(g.key)">
                  <td colspan="7">
                    <span class="rt-caret">{{ isOpen(g.key) ? '▾' : '▸' }}</span>
                    <b>{{ g.name }}</b>
                    <span class="rt-gcoach">{{ g.coach || '—' }}</span>
                    <span class="rt-gcount">{{ g.rows.length }} tin</span>
                    <span
                      v-for="c in g.chips"
                      :key="`${g.key}-${c.key}`"
                      class="rt-pill rt-pill-sm"
                      :class="`rt-s-${c.key}`"
                    >
                      <i class="rt-swatch"></i>{{ c.label }} {{ c.count }}
                    </span>
                  </td>
                </tr>

                <template v-if="isOpen(g.key)">
                  <template v-for="t in g.topics">
                    <tr :key="t.key" class="rt-gtopic" @click="toggleGroup(t.key)">
                      <td colspan="7" :class="topicClass(t.name)">
                        <span class="rt-caret">{{ isOpen(t.key) ? '▾' : '▸' }}</span>
                        <b class="rt-gtopic-name">{{ t.name }}</b>
                        <span class="rt-gtopic-num">{{ t.rows.length }}</span>
                      </td>
                    </tr>

                    <tr
                      v-for="row in isOpen(t.key) ? t.rows : []"
                      :key="row.id"
                      class="rt-grow"
                      :class="{ 'rt-row-on': selected && selected.id === row.id }"
                    >
                      <td class="rt-dim rt-num-cell">{{ row._timeLabel }}</td>
                      <td class="rt-dim rt-clip">{{ row.activityName || '—' }}</td>
                      <td>
                        <span
                          class="rt-chan"
                          :class="row.method === 2 ? 'rt-chan-app' : 'rt-chan-zalo'"
                        >
                          {{ methodText(row.method) }}
                        </span>
                      </td>
                      <td class="rt-dim rt-clip" :title="row.preview" colspan="2">
                        {{ row.preview || '—' }}
                      </td>
                      <td>
                        <span
                          class="rt-pill"
                          :class="`rt-s-${stateOf(row).key}`"
                          :title="row.stateReason"
                        >
                          <i class="rt-swatch"></i>{{ stateOf(row).label }}
                        </span>
                      </td>
                      <td class="rt-acts">
                        <button class="rt-rowbtn" :disabled="busy" @click="select(row)">
                          Chi tiết
                        </button>
                        <button
                          v-if="canReschedule(row)"
                          class="rt-rowbtn rt-rowbtn-fix"
                          :disabled="busy"
                          @click="reschedule(row)"
                        >
                          Đặt lại lịch
                        </button>
                        <button
                          v-if="row.state !== 5"
                          class="rt-rowbtn rt-rowbtn-del"
                          :disabled="busy"
                          @click="removeRow(row)"
                        >
                          Xoá
                        </button>
                      </td>
                    </tr>
                  </template>
                </template>
              </template>
            </tbody>

            <tbody v-else>
              <tr
                v-for="row in pagedItems"
                :key="row.id"
                :class="{ 'rt-row-on': selected && selected.id === row.id }"
              >
                <td>
                  <span class="rt-cust">{{ row.customerName || '— chưa gắn khách —' }}</span>
                  <span class="rt-code">{{ row.code || 'không qua vòng duyệt' }}</span>
                </td>
                <td>
                  <span
                    v-if="row.activityTopicName"
                    class="rt-topic"
                    :class="topicClass(row.activityTopicName)"
                  >
                    {{ row.activityTopicName }}
                  </span>
                  <span v-else class="rt-dim">—</span>
                </td>
                <td>
                  <span class="rt-chan" :class="row.method === 2 ? 'rt-chan-app' : 'rt-chan-zalo'">
                    {{ methodText(row.method) }}
                  </span>
                </td>
                <td class="rt-dim rt-clip" :title="row.preview">
                  {{ row.preview || row.activityName || '—' }}
                </td>
                <td class="rt-dim rt-num-cell">{{ row._timeLabel }}</td>
                <td>
                  <span class="rt-pill" :class="`rt-s-${stateOf(row).key}`" :title="row.stateReason">
                    <i class="rt-swatch"></i>{{ stateOf(row).label }}
                  </span>
                </td>
                <td class="rt-acts">
                  <button class="rt-rowbtn" :disabled="busy" @click="select(row)">
                    Chi tiết
                  </button>
                  <button
                    v-if="canReschedule(row)"
                    class="rt-rowbtn rt-rowbtn-fix"
                    :disabled="busy"
                    @click="reschedule(row)"
                  >
                    Đặt lại lịch
                  </button>
                  <button
                    v-if="row.state !== 5"
                    class="rt-rowbtn rt-rowbtn-del"
                    :disabled="busy"
                    @click="removeRow(row)"
                  >
                    Xoá
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="loading" class="rt-empty">Đang tải lịch gửi…</div>

          <div v-else-if="!sortedItems.length" class="rt-empty">
            <div class="rt-empty-t1">Không có tin nhắn nào khớp bộ lọc</div>
            <div class="rt-empty-t2">
              <template v-if="hasFilter">
                Thử bỏ bớt bộ lọc trạng thái hoặc kênh gửi, hoặc đổi khoảng ngày ở phía trên.
              </template>
              <template v-else-if="dayOffset !== 0">
                Đang xem {{ dateLabel }}. Bấm một nút khoảng ngày để quay về hôm nay.
              </template>
              <template v-else>
                Khoảng này chưa có nhắc nhở nào được đặt lịch.
              </template>
            </div>
          </div>
        </div>

        <div class="rt-foot">
          <span>{{ sortedItems.length }} dòng</span>

          <!-- Chỉ chế độ danh sách mới phân trang. Chế độ nhóm vốn đã thu gọn nên cắt
               trang ở đó là cắt ngang giữa một khách, đọc còn khó hơn. -->
          <span v-if="pageCount > 1" class="rt-pager">
            <button class="rt-ghost" :disabled="page <= 1" @click="page -= 1">‹ Trước</button>
            <span class="rt-pageinfo">
              Trang {{ page }}/{{ pageCount }}
              <span class="rt-dim">· {{ grouped ? 'theo khách' : 'theo dòng' }}</span>
            </span>
            <button class="rt-ghost" :disabled="page >= pageCount" @click="page += 1">
              Sau ›
            </button>
          </span>

        </div>
      </div>

      <b-modal
        v-model="reschedOpen"
        title="Đặt lại lịch gửi"
        ok-title="Đặt lại lịch"
        cancel-title="Huỷ"
        ok-variant="success"
        cancel-variant="light"
        :ok-disabled="busy"
        centered
        @ok.prevent="confirmReschedule"
      >
        <p class="mb-2">
          Nhắc nhở <b>{{ reschedTitle }}</b> của
          <b>{{ (reschedRow && reschedRow.customerName) || 'khách' }}</b>.
        </p>
        <label class="d-block mb-1 text-muted" style="font-size: 12.5px">Giờ gửi mới</label>
        <b-form-input v-model="reschedAt" type="datetime-local" />
        <p class="mt-2 mb-0 text-muted" style="font-size: 12.5px">
          {{ reschedNote }}
        </p>
      </b-modal>

      <!-- ===== Chi tiết ===== -->
      <aside v-if="selected" class="rt-detail">
        <div class="rt-dhead">
          <div>
            <div class="rt-dtitle">{{ selected.customerName || '— chưa gắn khách —' }}</div>
            <div class="rt-dsub">{{ selected.code || 'Không qua vòng duyệt' }}</div>
          </div>
          <button class="rt-iconbtn" aria-label="Đóng" @click="selected = null">×</button>
        </div>

        <div class="rt-dstate" :class="`rt-s-${stateOf(selected).key}`">
          <i class="rt-swatch"></i>
          {{ stateOf(selected).label }}
          <template v-if="selected.stateReason"> — {{ selected.stateReason }}</template>
        </div>

        <div class="rt-facts">
          <div><span>Giờ gửi</span><b>{{ fmtDateTime(selected.sendAt) }}</b></div>
          <div><span>Hoạt động</span><b>{{ selected.activityName || '—' }}</b></div>
          <div><span>Kênh</span><b>{{ methodText(selected.method) }}</b></div>
          <div><span>Coach</span><b>{{ selected.coachName || '—' }}</b></div>
          <div><span>Nhóm Zalo</span><b>{{ selected.zaloGroupId || '—' }}</b></div>
        </div>

        <div class="rt-dsection">Diễn biến</div>
        <div v-for="(ev, i) in timelineOf(selected)" :key="i" class="rt-tl" :class="{ 'rt-tl-future': ev.future }">
          <span>{{ ev.future ? '○' : '●' }}</span>
          <span class="rt-tl-time">{{ ev.time }}</span>
          <span>{{ ev.text }}</span>
        </div>

        <div class="rt-dsection">Nội dung</div>
        <div v-if="detailLoading" class="rt-dim">Đang tải nội dung…</div>
        <template v-else-if="detail">
          <template v-for="part in contentParts">
            <div :key="part.label" class="rt-cbox">
              <div class="rt-clabel">{{ part.label }}</div>
              <div class="rt-cbody">{{ part.text }}</div>
            </div>
          </template>
        </template>
        <div v-else class="rt-cbox">
          <div class="rt-clabel">Sẽ gửi</div>
          <div class="rt-cbody">{{ selected.preview || '—' }}</div>
        </div>

        <!-- Ảnh của hoạt động. Job gửi thật đính kèm ảnh này cùng phần chữ, nên duyệt mà
             chỉ thấy chữ là duyệt thiếu đúng một nửa thứ khách sẽ nhận. -->
        <div v-if="isImageLink(selected.detailLink)" class="rt-cbox">
          <div class="rt-clabel">Hình đi kèm</div>
          <img
            class="rt-cimg"
            :src="selected.detailLink"
            alt="Hình đi kèm hoạt động"
            @error="$event.target.style.display = 'none'"
          />
        </div>

        <!-- Duyệt ngay tại đây, KHÔNG phụ thuộc chế độ duyệt của gói. Gói "duyệt từng
             tin" vẫn có đường chốt qua Zalo, nhưng coach đang mở màn này thì bắt họ đi
             vòng sang Zalo chỉ để bấm một nút là vô nghĩa — và lúc Zalo hỏng thì đó là
             đường duy nhất còn lại. API tự biết trạng thái hiện tại nên duyệt ở đâu cũng
             ra cùng một kết quả. -->
        <template v-if="canReview(selected)">
          <div class="rt-dsection">Duyệt</div>

          <b-form-textarea
            v-model="reviewText"
            class="rt-rtext"
            rows="5"
            placeholder="Nội dung sẽ gửi cho khách"
          ></b-form-textarea>

          <div class="rt-racts">
            <button
              class="rt-rowbtn rt-rowbtn-ok"
              :disabled="busy"
              @click="submitReview(reviewEdited ? 2 : 0)"
            >
              {{ reviewEdited ? 'Duyệt bản sửa' : 'Duyệt' }}
            </button>
            <button class="rt-rowbtn rt-rowbtn-del" :disabled="busy" @click="submitReview(1)">
              Bỏ qua
            </button>
            <button v-if="reviewEdited" class="rt-rowbtn" :disabled="busy" @click="resetReviewText">
              Hoàn tác sửa
            </button>
          </div>
        </template>

        <div class="rt-dsection">Lịch gửi</div>
        <!-- Các bước, không phải một đoạn văn: câu hỏi duy nhất ở đây là "đang đứng ở
             đâu và còn thiếu bước nào". Giá trị thô để trong tooltip cho người sửa lỗi. -->
        <div
          class="rt-steps"
          :title="`job_id = ${selected.jobId || '(rỗng)'} · job status = ${selected.jobStatus}`"
        >
          <div
            v-for="(st, i) in scheduleSteps(selected)"
            :key="i"
            class="rt-step"
            :class="`is-${st.status}`"
          >
            <span class="rt-step-dot">{{ st.icon }}</span>
            <span>{{ st.text }}</span>
          </div>
        </div>

        <!-- Lý do hỏng, lấy thẳng từ lượt đặt lịch gần nhất. Chỉ hiện với dòng đang
             hỏng: với dòng bình thường đây là phản hồi thô của Hangfire, không ai cần. -->
        <div v-if="selected.jobNote && [2, 6].includes(selected.state)" class="rt-jobnote">
          {{ selected.jobNote }}
        </div>

        <!-- Cuối panel và tách hẳn ra: thao tác này không hoàn lại được, để cạnh mấy
             nút dùng hằng ngày ở trên là sớm muộn cũng có người bấm nhầm. -->
        <template v-if="selected.state !== 5">
          <div class="rt-dsection">Gỡ khỏi lộ trình</div>
          <button class="rt-rowbtn rt-rowbtn-del" :disabled="busy" @click="removeRow(selected)">
            Xoá hoạt động này
          </button>
          <p class="rt-dangernote">
            Xoá luôn lịch gửi trên Hangfire<template v-if="selected.code"> và bản ghi
            duyệt</template>. Không khôi phục lại được.
          </p>
        </template>
      </aside>
    </div>
  </div>
</template>

<script>
import reminderAiService from '@/view/pages/reminder_ai/services/reminderAiService';

// Portal ghim moment về Asia/Ho_Chi_Minh (core/plugins/moment.js). Mọi giờ hiển thị đi
// qua moment chứ KHÔNG dùng Date.getHours() — getHours() đọc theo múi giờ của MÁY.
const VN_TZ = 'Asia/Ho_Chi_Minh';

const RANGES = [
  { days: 1, text: 'Hôm nay' },
  { days: 3, text: '3 ngày' },
  { days: 7, text: 'Tuần' },
  // BE chặn cứng ở 31 ngày một lượt (MaxScheduleDays) — xin hơn cũng bị cắt về đó.
  { days: 30, text: '30 ngày' },
];

// Thứ tự trùng enum ScheduleState bên BE — value chính là số BE trả về.
const STATES = [
  { key: 'will', value: 0, label: 'Sẽ gửi' },
  { key: 'wait', value: 1, label: 'Chờ coach' },
  { key: 'stuck', value: 2, label: 'Lỗi' },
  { key: 'pending', value: 3, label: 'Chưa duyệt' },
  { key: 'skip', value: 4, label: 'Không gửi' },
  { key: 'sent', value: 5, label: 'Đã gửi' },
  { key: 'failed', value: 6, label: 'Lỗi hệ thống' },
];

export default {
  name: 'ReminderTrackingList',

  data() {
    return {
      RANGES,
      loading: false,
      busy: false,

      dayOffset: 0,
      rangeDays: 1,

      // Khoá theo một gói khi mở từ màn gói dịch vụ. Lọc ở BE chứ không lọc ở FE:
      // thanh đếm trạng thái phải đếm đúng phần đang xem, mà nó đếm trên dữ liệu
      // BE trả về.
      lockUserPackageId: null,
      lockLabel: '',

      search: '',

      // UserPackageContentMode dạng chuỗi, cùng quy ước với methods. Mặc định chỉ
      // AI Mode: màn này sinh ra để theo dõi vòng duyệt của nó. Gói thường gửi thẳng,
      // không có gì để theo dõi ngoài đã gửi hay chưa — bật sẵn cả hai là mọi con số
      // trạng thái bị pha loãng bởi những dòng không ai cần nhìn.
      modes: ['1'],

      coachFilter: 'all',
      accountFilter: 'all',
      // Mặc định chỉ Zalo: đây là kênh đi qua vòng duyệt của coach, cũng là kênh
      // duy nhất có trạng thái đáng theo dõi. Nhắc nhở App gửi thẳng, tick thêm
      // khi cần đối chiếu.
      methods: ['1'],
      activeState: null,
      sortMode: 'time_asc',

      items: [],

      selected: null,
      detail: null,
      detailLoading: false,
      // Nội dung trong ô duyệt. So với bản gốc để biết coach có sửa hay không —
      // gửi action EDIT kèm đúng chữ cũ là ghi đè edit_percent bằng một lần sửa giả.
      reviewText: '',
      reviewOriginal: '',

      // Nhóm theo khách là mặc định: khoảng 30 ngày thường ra hơn nghìn dòng, phẳng
      // thì không dùng được. openKeys rỗng = tất cả đang thu gọn.
      grouped: true,
      openKeys: {},

      page: 1,
      pageSize: 50,
      groupPageSize: 15,

      reschedOpen: false,
      reschedRow: null,
      reschedAt: '',

      now: new Date(),
      timerId: null,
      clockId: null,
    };
  },

  computed: {
    // Ranh giới ngày theo giờ VN, không theo máy.
    baseDay() {
      return this.$moment().tz(VN_TZ).startOf('day').add(this.dayOffset, 'day').toDate();
    },

    rangeEnd() {
      return this.$moment(this.baseDay).tz(VN_TZ).add(this.rangeDays, 'day').toDate();
    },

    dateLabel() {
      const from = this.$moment(this.baseDay).tz(VN_TZ);
      if (this.rangeDays === 1) return from.format('DD/MM/YYYY');

      const to = this.$moment(this.baseDay).tz(VN_TZ).add(this.rangeDays - 1, 'day');
      return `${from.format('DD/MM')} – ${to.format('DD/MM/YYYY')}`;
    },

    rangeSub() {
      const r = RANGES.find((x) => x.days === this.rangeDays);
      return `${r ? r.text : ''} · ${this.dateLabel}`;
    },

    clockText() {
      return this.$moment(this.now).tz(VN_TZ).format('HH:mm');
    },

    hasFilter() {
      return (
        this.activeState !== null ||
        this.methods.length < 2 ||
        this.modes.length < 2 ||
        !!this.search ||
        this.coachFilter !== 'all' ||
        this.accountFilter !== 'all'
      );
    },

    // Cắt theo chế độ gói TRƯỚC mọi thứ khác, rồi mọi chỗ khác đều đọc từ đây: bộ đếm
    // trạng thái, dropdown coach, dropdown khách. Lọc riêng lẻ từng chỗ là dropdown
    // liệt kê những coach không còn dòng nào hiện ra.
    modeItems() {
      if (this.modes.length >= 2) return this.items;

      // contentMode thiếu (dòng cũ, BE bản trước) tính là gói thường — đoán ngược lại
      // là dòng không rõ nguồn gốc lọt vào danh sách AI Mode.
      return this.items.filter((row) => this.modes.includes(String(row.contentMode || 0)));
    },

    // Dòng không qua vòng duyệt thì không có mã RMD — rơi về tên hoạt động, chứ đừng
    // in một ô trống rồi để coach đoán mình đang đổi giờ cho cái gì.
    reschedTitle() {
      const row = this.reschedRow;
      if (!row) return '';
      return row.code || row.activityName || 'nhắc nhở này';
    },

    // Mỗi trạng thái vướng một thứ khác nhau. Trước đây nút này chỉ hiện với dòng lỗi
    // nên một câu là đủ; giờ nó hiện ở mọi dòng chưa gửi, dùng lại câu cũ là nói sai
    // với phần lớn trường hợp.
    reschedNote() {
      const row = this.reschedRow;
      if (!row) return '';

      const old = this.fmtDateTime(row.sendAt);

      if (row.state === 0) {
        return `Đang có lịch gửi lúc ${old}. Đổi giờ sẽ huỷ lịch cũ và đặt lại theo giờ mới.`;
      }
      if (row.state === 1) {
        return `Đang chờ coach xác nhận qua Zalo cho giờ ${old}. `
          + 'Đổi giờ thì lượt hỏi cũng dời theo.';
      }
      if (row.state === 3) {
        return 'Nhắc nhở chưa được duyệt. Đặt lại lịch sẽ chuyển nó sang chờ coach xác '
          + 'nhận qua Zalo trước giờ gửi mới — trừ gói duyệt trước, gói đó vẫn phải '
          + 'duyệt ở đây.';
      }
      if (row.state === 4) {
        return 'Nhắc nhở đang ở trạng thái không gửi. Đặt lại lịch sẽ cho nó chạy lại '
          + 'vòng gửi từ đầu với giờ mới — nội dung đã duyệt giữ nguyên.';
      }
      return `Giờ cũ ${old} đã qua hoặc không đặt được lịch. `
        + 'Nội dung đã duyệt giữ nguyên, không phải duyệt lại.';
    },

    // Tập nền cho bộ đếm: đã áp kênh / coach / tìm kiếm nhưng CHƯA áp trạng thái.
    // Bấm một nhãn mà các nhãn khác về 0 thì mất luôn bức tranh chung.
    baseItems() {
      const q = this.search.trim().toLowerCase();

      return this.modeItems.filter((row) => {
        if (row.method != null && !this.methods.includes(String(row.method))) return false;
        if (this.coachFilter !== 'all' && row.coachId !== this.coachFilter) return false;
        if (this.accountFilter !== 'all' && row.accountId !== this.accountFilter) return false;

        if (q) {
          const code = (row.code || '').toLowerCase();
          const name = (row.customerName || '').toLowerCase();
          if (code.indexOf(q) < 0 && name.indexOf(q) < 0) return false;
        }
        return true;
      });
    },

    visibleItems() {
      if (this.activeState === null) return this.baseItems;
      return this.baseItems.filter((row) => row.state === this.activeState);
    },

    sortedItems() {
      const rows = this.visibleItems.slice();
      const at = (x) => (x._at ? x._at.getTime() : 0);

      if (this.sortMode === 'time_asc') return rows.sort((a, b) => at(a) - at(b));
      if (this.sortMode === 'time_desc') return rows.sort((a, b) => at(b) - at(a));
      if (this.sortMode === 'name') {
        return rows.sort((a, b) =>
          String(a.customerName || '').localeCompare(String(b.customerName || ''), 'vi'),
        );
      }
      return rows.sort((a, b) => a.state - b.state || at(a) - at(b));
    },

    stateChips() {
      return STATES.map((s) => ({
        ...s,
        count: this.baseItems.filter((row) => row.state === s.value).length,
      }));
    },

    customerCount() {
      return new Set(this.visibleItems.map((x) => x.accountId).filter(Boolean)).size;
    },

    coachOptions() {
      const seen = new Map();
      this.modeItems.forEach((x) => {
        if (x.coachId && !seen.has(x.coachId)) {
          // Thiếu tên thì hiện nhãn đọc được, KHÔNG đổ GUID ra. Id vẫn giữ nguyên để
          // lọc; dán một chuỗi 36 ký tự vào dropdown thì không ai chọn đúng được.
          seen.set(x.coachId, { id: x.coachId, name: x.coachName || '— coach chưa có tên —' });
        }
      });
      return Array.from(seen.values()).sort((a, b) =>
        String(a.name).localeCompare(String(b.name), 'vi'),
      );
    },

    // Danh sách khách LỌC THEO COACH đang chọn: chọn coach xong mà ô khách vẫn liệt kê
    // toàn bộ khách của mọi coach thì phải tự mò xem ai của ai.
    customerOptions() {
      const seen = new Map();

      this.modeItems.forEach((x) => {
        if (!x.accountId) return;
        if (this.coachFilter !== 'all' && x.coachId !== this.coachFilter) return;
        if (!seen.has(x.accountId)) {
          seen.set(x.accountId, {
            id: x.accountId,
            name: x.customerName || '— chưa có tên —',
          });
        }
      });

      return Array.from(seen.values()).sort((a, b) =>
        String(a.name).localeCompare(String(b.name), 'vi'),
      );
    },

    // Nhãn nút cho các b-dropdown lọc/sắp xếp
    coachFilterLabel() {
      if (this.coachFilter === 'all') return `Tất cả (${this.coachOptions.length})`;
      const c = this.coachOptions.find((x) => x.id === this.coachFilter);
      return c ? c.name : '';
    },
    accountFilterLabel() {
      if (this.accountFilter === 'all') return `Tất cả (${this.customerOptions.length})`;
      const c = this.customerOptions.find((x) => x.id === this.accountFilter);
      return c ? c.name : '';
    },
    sortModeOptions() {
      return [
        { value: 'time_asc', text: 'Thời gian: sắp gửi trước' },
        { value: 'time_desc', text: 'Thời gian: xa nhất trước' },
        { value: 'name', text: 'Tên khách hàng A–Z' },
        { value: 'status', text: 'Trạng thái' },
      ];
    },
    sortModeLabel() {
      const o = this.sortModeOptions.find((x) => x.value === this.sortMode);
      return o ? o.text : '';
    },

    // Khách -> chủ đề -> từng giờ gửi. Giữ nguyên thứ tự của sortedItems nên mọi
    // lựa chọn sắp xếp / lọc ở trên vẫn có tác dụng bên trong nhóm.
    groups() {
      const byCustomer = new Map();

      this.sortedItems.forEach((row) => {
        const ck = `c:${row.accountId || row.customerName || 'none'}`;

        if (!byCustomer.has(ck)) {
          byCustomer.set(ck, {
            key: ck,
            name: row.customerName || '— chưa gắn khách —',
            coach: row.coachName,
            rows: [],
            topicMap: new Map(),
          });
        }

        const g = byCustomer.get(ck);
        g.rows.push(row);

        // Hoạt động không gắn chủ đề gom vào "Khác" — nhóm tên trống trông như lỗi.
        const tName = row.activityTopicName || 'Khác';
        const tk = `${ck}|t:${tName}`;

        if (!g.topicMap.has(tk)) g.topicMap.set(tk, { key: tk, name: tName, rows: [] });
        g.topicMap.get(tk).rows.push(row);
      });

      return [...byCustomer.values()].map((g) => ({
        key: g.key,
        name: g.name,
        coach: g.coach,
        rows: g.rows,
        topics: [...g.topicMap.values()],
        // Chỉ hiện nhãn có mặt thật, để dòng tiêu đề không thành một hàng số 0.
        chips: STATES
          .map((st) => ({
            key: st.key,
            label: st.label,
            count: g.rows.filter((r) => r.state === st.value).length,
          }))
          .filter((c) => c.count > 0),
      }));
    },

    // Chế độ nhóm cắt trang theo KHÁCH, không theo dòng: cắt theo dòng thì một khách
    // bị xẻ đôi giữa hai trang, mất luôn ý nghĩa của việc gom nhóm.
    pageCount() {
      const total = this.grouped ? this.groups.length : this.sortedItems.length;
      const size = this.grouped ? this.groupPageSize : this.pageSize;
      return Math.max(1, Math.ceil(total / size));
    },

    pagedGroups() {
      const start = (this.page - 1) * this.groupPageSize;
      return this.groups.slice(start, start + this.groupPageSize);
    },

    pagedItems() {
      const start = (this.page - 1) * this.pageSize;
      return this.sortedItems.slice(start, start + this.pageSize);
    },

    allGroupsOpen() {
      return this.groups.length > 0 && this.groups.every((g) => this.openKeys[g.key]);
    },

    reviewEdited() {
      return this.reviewText.trim() !== this.reviewOriginal.trim();
    },

    contentParts() {
      const d = this.detail || {};
      return [
        { label: 'Gốc chương trình', text: d.sourceContent },
        { label: 'Bản AI', text: d.originalContent },
        { label: 'Coach sửa', text: d.editedContent },
      ].filter((x) => !!x.text);
    },
  },

  watch: {
    // Lọc xong mà vẫn đứng ở trang 7 thì bảng trống trơn, người dùng tưởng không có
    // dữ liệu. Về trang 1 mỗi khi tập dòng đổi.
    sortedItems() {
      if (this.page > this.pageCount) this.page = 1;
    },

    // Hai chế độ đếm trang theo hai đơn vị khác nhau, nên trang 8 của chế độ này không
    // tương ứng gì với trang 8 của chế độ kia.
    grouped() {
      this.page = 1;
    },

    // Đổi chế độ gói là đổi hẳn tập coach và khách bên dưới. Giữ lại lựa chọn cũ thì
    // nó có thể trỏ vào người không còn dòng nào, và bảng rỗng mà không rõ vì sao.
    modes() {
      this.coachFilter = 'all';
      this.accountFilter = 'all';
    },

    // Đổi coach là bỏ khách đang chọn: giữ lại thì bộ lọc thành "khách A VÀ coach B"
    // không liên quan tới nhau, và bảng rỗng mà không rõ vì sao.
    coachFilter() {
      this.accountFilter = 'all';
    },
  },

  methods: {
    // BE trả DateTime không kèm múi giờ (cột datetime của MySQL lưu UTC, EF đọc ra
    // Kind=Unspecified) — JS hiểu chuỗi không offset là GIỜ ĐỊA PHƯƠNG, lệch 7 tiếng.
    // Thử cả hai dạng rồi mới chịu thua: chuỗi lạ mà trả Invalid Date thì cả cột giờ
    // lẫn sắp xếp đều hỏng, mà lỗi lại hiện ra thành chữ "Invalid date" trong bảng.
    toUtcDate(value) {
      if (!value) return null;
      if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

      if (typeof value === 'number') {
        const n = new Date(value < 1e12 ? value * 1000 : value);
        return isNaN(n.getTime()) ? null : n;
      }

      const s = String(value).trim();
      if (!s) return null;

      const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(s);
      let d = new Date(hasZone ? s : `${s}Z`);
      if (isNaN(d.getTime())) d = new Date(s);

      return isNaN(d.getTime()) ? null : d;
    },

    fmtTime(value) {
      const d = this.toUtcDate(value);
      return d ? this.$moment(d).tz(VN_TZ).format('HH:mm') : '—';
    },

    fmtDateTime(value) {
      const d = this.toUtcDate(value);
      return d ? this.$moment(d).tz(VN_TZ).format('HH:mm DD/MM/YYYY') : '—';
    },

    stateOf(row) {
      return STATES.find((s) => s.value === row.state) || { key: 'pending', label: '—' };
    },

    methodText(method) {
      if (method === 1) return 'Zalo';
      if (method === 2) return 'App';
      return '—';
    },

    timelineOf(row) {
      const out = [];
      const push = (value, text, future = false) =>
        out.push({ time: this.fmtDateTime(value), text, future });

      if (row.reviewedAt) {
        push(row.reviewedAt, `Coach duyệt (${row.reviewedChannel === 1 ? 'Zalo' : 'portal'})`);
      }
      if (row.notifiedAt) push(row.notifiedAt, 'Hỏi coach xác nhận lần cuối');
      if (row.sentAt) push(row.sentAt, 'Đã gửi vào nhóm');

      if (!row.sentAt) {
        const future = !!row._at && row._at > this.now;
        push(row.sendAt, future ? 'Gửi cho khách (chưa xảy ra)' : 'Tới giờ gửi');
      }
      return out;
    },

    // Trạng thái lịch gửi diễn giải thành câu. Bày "job status = 4" ra cho coach thì
    // vừa không hiểu, vừa tưởng hỏng — trong khi với gói duyệt-từng-tin thì job rỗng
    // là đúng thiết kế, job chỉ sinh sau khi coach xác nhận.
    // Chặng đường của một nhắc nhở, dạng bước. Bước đang đứng mang trạng thái riêng,
    // bước chưa tới để mờ — nhìn một cái là biết còn thiếu gì, khỏi đọc câu văn.
    scheduleSteps(row) {
      const asked = !!row.notifiedAt;

      // Gói "duyệt trước nội dung" không đi qua vòng hỏi lại nào. Bỏ hẳn hai bước đó
      // khỏi danh sách chứ không hiện rồi gạch: bước không bao giờ chạy mà vẫn nằm đó
      // chỉ làm người đọc tưởng quy trình đang kẹt.
      const viaZalo = asked || row.state === 1 || row.state === 2;

      const labels = viaZalo
        ? ['Duyệt nội dung', 'Hỏi coach qua Zalo', 'Coach xác nhận', 'Đặt lịch gửi', 'Gửi cho khách']
        : ['Duyệt nội dung', 'Đặt lịch gửi', 'Gửi cho khách'];

      const last = labels.length - 1;
      const jobIdx = last - 1;

      // [vị trí đang đứng, chuyện gì xảy ra ở đó]
      let at = 0;
      let outcome = 'now';

      switch (row.state) {
        case 0: at = last; outcome = 'wait'; break;
        // Chưa hỏi thì đang đứng ở chính bước hỏi, không phải bước chờ trả lời.
        case 1: at = asked ? 2 : (viaZalo ? 1 : 0); outcome = 'now'; break;
        case 2: at = jobIdx; outcome = 'fail'; break;
        case 3: at = 0; outcome = 'now'; break;
        case 4: at = viaZalo ? 2 : 0; outcome = 'stop'; break;
        case 5: at = last; outcome = 'done'; break;
        case 6: at = last; outcome = 'fail'; break;
        default: at = 0; outcome = 'now'; break;
      }

      // CHỈ dấu đặc ● mới là xong. Trước đây bước đang chờ dùng ◍ — nhìn cũng đặc,
      // nên "Duyệt nội dung" của một tin chưa ai duyệt trông y như đã duyệt xong.
      const ICONS = {
        done: '●', now: '○', wait: '○', fail: '✕', stop: '—', todo: '○',
      };

      return labels.map((text, i) => {
        const status = i < at ? 'done' : (i === at ? outcome : 'todo');
        let label = text;

        if (i === last && status === 'done' && row.sentAt) {
          label = `Đã gửi lúc ${this.fmtTime(row.sentAt)}`;
        } else if (i === jobIdx && status === 'fail') {
          label = 'Đặt lịch gửi — hỏng, bấm “Đặt lại lịch”';
        } else if (status === 'now') {
          // Nói thẳng ra chứ không để màu sắc tự giải thích: đúng một bước mang nhãn
          // này tại một thời điểm, nên thêm chữ không làm danh sách rối.
          label = `${label} — chưa xong`;
        } else if (status === 'wait') {
          label = `${label} — chờ tới giờ`;
        }

        return { text: label, status, icon: ICONS[status] };
      });
    },

    toggleState(value) {
      this.activeState = this.activeState === value ? null : value;
    },

    shiftDay(step) {
      this.dayOffset += step * this.rangeDays;
      this.loadData();
    },

    // Đổi khoảng thì kéo luôn về hôm nay — nút tên "Hôm nay" mà vẫn đứng ở quá khứ
    // sau vài lần bấm ‹ là vô lý.
    setRange(days) {
      this.rangeDays = days;
      this.dayOffset = 0;
      this.loadData();
    },

    // "Đặt lại lịch" chỉ hiện với nhãn Lỗi — dòng đã duyệt mà không có job, sẽ
    // không bao giờ gửi. Không gửi ngay: dựng lại job cho đúng giờ đã hẹn.
    reschedule(row) {
      this.reschedRow = row;

      // Giờ cũ nếu còn ở tương lai, không thì 15 phút nữa. Đổ sẵn giờ đã qua là coach
      // bấm OK rồi nhận đúng lỗi "giờ gửi đã qua" mà nút này sinh ra để gỡ.
      const old = this.toUtcDate(row.sendAt);
      const soon = this.$moment().tz(VN_TZ).add(15, 'minutes');
      const base = old && this.$moment(old).tz(VN_TZ).isAfter(soon)
        ? this.$moment(old).tz(VN_TZ)
        : soon;

      this.reschedAt = base.format('YYYY-MM-DDTHH:mm');
      this.reschedOpen = true;
    },

    async confirmReschedule() {
      const row = this.reschedRow;
      if (!row) return;

      const sendAt = this.reschedAt ? this.$moment(this.reschedAt).unix() : null;

      this.busy = true;
      try {
        const res = await reminderAiService.rescheduleSendJob(row.id, sendAt);
        this.reschedOpen = false;
        this.$toastr.s({
          title: res && res.scheduled ? 'Đã đặt lịch' : 'Đã ghi nhận',
          msg: (res && res.message) || 'Xong',
        });
        await this.loadData();
      } catch (e) {
        this.$toastr.e({
          title: 'Không đặt lại được',
          msg: (e && e.message) || 'Lỗi không xác định',
        });
      } finally {
        this.busy = false;
      }
    },

    // Xoá hẳn khỏi lộ trình. API đã lo huỷ job Hangfire, dọn nháp Redis và hai set
    // xác nhận — không có gì phải dọn thêm ở đây.
    async removeRow(row) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Xoá nhắc nhở lúc ${row._timeLabel} của "${row.customerName || 'khách'}"? `
          + `Lịch gửi trên Hangfire${row.code ? ' và bản ghi duyệt' : ''} bị xoá theo. `
          + 'Tin này sẽ không được gửi và không khôi phục lại được.',
        {
          title: 'Xác nhận xoá',
          okTitle: 'Xoá',
          okVariant: 'danger',
          cancelTitle: 'Huỷ',
          cancelVariant: 'light',
          centered: true,
        },
      );
      if (!ok) return;

      this.busy = true;
      try {
        await reminderAiService.deletePlanned(row.id);
        if (this.selected && this.selected.id === row.id) this.selected = null;

        this.$toastr.s({ title: 'Đã xoá', msg: 'Nhắc nhở đã được gỡ khỏi lộ trình' });
        await this.loadData();
      } catch (e) {
        this.$toastr.e({
          title: 'Không xoá được',
          msg: (e && e.message) || 'Lỗi không xác định',
        });
      } finally {
        this.busy = false;
      }
    },

    // Duyệt được khi nhắc nhở còn qua vòng duyệt và chưa chốt xong. Đã gửi (5) hoặc
    // đã bỏ/hết hạn (4) thì không, dòng không có mã (nhắc nhở App, gói thường) cũng không.
    canReview(row) {
      return !!(row && row.code && [0, 1, 2, 3].includes(row.state));
    },

    // Mọi trạng thái trừ Đã gửi. Đổi giờ là việc hợp lệ với cả dòng sắp gửi lẫn dòng
    // chưa duyệt — chỉ dòng đã tới tay khách là không lùi lại được. BE cũng nới theo:
    // dòng đã có job thì huỷ job cũ rồi đặt lại, dòng chưa duyệt thì chỉ đổi giờ và
    // nói rõ còn vướng gì.
    canReschedule(row) {
      return !!row && row.state !== 5;
    },

    // Màu theo TÊN chủ đề chứ không theo thứ tự xuất hiện: cùng một chủ đề phải giữ
    // nguyên màu ở mọi khách, mọi khoảng ngày, mọi lần tải lại. Đánh số theo vị trí thì
    // "Dinh dưỡng" hôm nay xanh, mai đổi sang cam vì khách đầu danh sách khác đi.
    topicClass(name) {
      const text = String(name || '');
      let hash = 0;
      for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) % 997;
      return `rt-t${hash % 8}`;
    },

    // detail_link phần lớn KHÔNG phải URL: nó giữ đích deeplink của app
    // (ScheduleMenuFood, ScheduleBloodSugar…) để thông báo App bấm vào thì mở đúng màn.
    // Chỉ số ít hoạt động điền URL ảnh vào đó, và chỉ những cái đó mới đáng hiện.
    isImageLink(link) {
      return !!link && /^https?:\/\//i.test(link);
    },

    // Khoá ô cuối cùng còn tick thay vì để bỏ hết rồi tự khôi phục: bấm mà không có
    // gì xảy ra thì người dùng tưởng hỏng, còn ô mờ đi thì thấy ngay là không bỏ được.
    isOnlyMode(value) {
      return this.modes.length === 1 && this.modes[0] === value;
    },

    isOpen(key) {
      return !!this.openKeys[key];
    },

    toggleGroup(key) {
      this.$set(this.openKeys, key, !this.openKeys[key]);
    },

    // Mở tất cả = mở cả cấp khách lẫn cấp chủ đề. Mở mỗi cấp trên thì vẫn phải bấm
    // thêm 27 lần nữa, không gọi là "mở tất cả" được.
    toggleAllGroups() {
      const open = !this.allGroupsOpen;
      const next = {};

      if (open) {
        this.groups.forEach((g) => {
          next[g.key] = true;
          g.topics.forEach((t) => { next[t.key] = true; });
        });
      }

      this.openKeys = next;
    },

    resetReviewText() {
      this.reviewText = this.reviewOriginal;
    },

    async submitReview(action) {
      const row = this.selected;
      if (!row || !row.code) return;

      const content = this.reviewText.trim();

      if (action === 2 && !content) {
        this.$toastr.e({ title: 'Thiếu nội dung', msg: 'Nhập nội dung trước khi duyệt bản sửa' });
        return;
      }

      const labels = { 0: 'Duyệt nguyên bản', 1: 'Bỏ qua', 2: 'Duyệt bản sửa' };

      // Cùng quy ước với các modal khác của portal: xanh lá cho hành động chính,
      // đỏ cho hành động phá huỷ. Để mặc định thì ra btn-primary nhạt, lệch hẳn
      // với những popup còn lại.
      const ok = await this.$bvModal.msgBoxConfirm(
        `${labels[action]} nhắc nhở ${row.code} của "${row.customerName || 'khách'}"?`,
        {
          title: 'Xác nhận',
          okTitle: labels[action],
          okVariant: action === 1 ? 'danger' : 'success',
          cancelTitle: 'Huỷ',
          cancelVariant: 'light',
          centered: true,
        },
      );
      if (!ok) return;

      this.busy = true;
      try {
        await reminderAiService.review({
          code: row.code,
          action,
          editedContent: action === 2 ? content : null,
          // Duyệt ở portal, kể cả khi gói đang ở chế độ chốt qua Zalo — ghi đúng nơi
          // coach thực sự bấm, không thì thống kê kênh duyệt sai.
          channel: 0,
          // Bấm ở đây là CHỐT, không phải duyệt rồi chờ hỏi lại qua Zalo. Thiếu cờ này
          // thì dòng đang "Chờ coach" bấm Duyệt xong vẫn y nguyên "Chờ coach".
          forceConfirm: true,
        });

        this.$toastr.s({ title: labels[action], msg: `Đã ghi nhận cho ${row.code}` });
        await this.loadData();
        if (this.selected) await this.select(this.selected);
      } catch (e) {
        this.$toastr.e({
          title: 'Không duyệt được',
          msg: (e && e.message) || 'Lỗi không xác định',
        });
      } finally {
        this.busy = false;
      }
    },

    async select(row) {
      this.selected = row;
      this.detail = null;
      this.reviewText = '';
      this.reviewOriginal = '';
      if (!row.code) return;

      try {
        this.detailLoading = true;
        this.detail = await reminderAiService.getByCode(row.code);

        // Đổ đúng nội dung SẼ gửi: bản coach sửa nếu có, không thì bản AI. Đổ bản AI
        // khi đã có bản sửa là coach bấm Duyệt rồi mất công sửa lần trước.
        const d = this.detail || {};
        this.reviewOriginal = d.finalContent || d.editedContent || d.originalContent || '';
        this.reviewText = this.reviewOriginal;
      } catch (e) {
        this.detail = null;
      } finally {
        this.detailLoading = false;
      }
    },

    clearLock() {
      this.lockUserPackageId = null;
      this.lockLabel = '';
      // Gỡ khỏi URL luôn: để nguyên thì F5 hay lưu link lại khoá về gói cũ, mà thanh
      // khoá đã biến mất nên không còn gì nói cho người dùng biết vì sao bảng thiếu.
      const query = { ...this.$route.query };
      delete query.userPackageId;
      delete query.label;
      this.$router.replace({ query }).catch(() => {});

      this.loadData();
    },

    async loadData() {
      try {
        this.loading = true;

        const params = {
          from: Math.floor(this.baseDay.getTime() / 1000),
          to: Math.floor(this.rangeEnd.getTime() / 1000),
        };
        if (this.lockUserPackageId) params.userPackageId = this.lockUserPackageId;

        const res = await reminderAiService.getSchedule(params);

        this.items = (res && res.items ? res.items : []).map((x) => ({
          ...x,
          _at: this.toUtcDate(x.sendAt),
          _timeLabel: this.fmtTime(x.sendAt),
        }));


        if (this.selected) {
          this.selected = this.items.find((x) => x.id === this.selected.id) || null;
        }
      } catch (e) {
        this.$toastr.e({
          title: 'Lỗi',
          msg: (e && e.message) || 'Không tải được lịch gửi',
        });
      } finally {
        this.loading = false;
      }
    },
  },

  created() {
    const query = this.$route.query || {};
    if (query.userPackageId) {
      this.lockUserPackageId = query.userPackageId;
      this.lockLabel = query.label || '';
      // Lịch của một gói trải nhiều tuần. Mở ở "hôm nay" thì gần như chắc chắn ra
      // bảng rỗng, và người vừa bấm từ màn gói sẽ tưởng gói mình không có nhắc nhở nào.
      this.rangeDays = 30;
    }

    this.loadData();
    this.clockId = setInterval(() => { this.now = new Date(); }, 30000);
    // Tự làm mới ngầm mỗi phút. Không bày nút bật/tắt: đây là màn theo dõi, dữ liệu
    // cũ mà trông như mới là thứ tệ nhất nó có thể làm.
    this.timerId = setInterval(() => {
      if (!this.loading && !this.busy) this.loadData();
    }, 60000);
  },

  beforeDestroy() {
    clearInterval(this.clockId);
    clearInterval(this.timerId);
  },
};
</script>

<style lang="scss" scoped>
// MỌI class đều mang tiền tố rt-. Bản trước dùng tên chung (.app .body .main .badge
// .chip .switch .dot .count) và bị CSS toàn cục của Metronic/Bootstrap đè — dòng bảng
// cao 700px, badge phình thành khối bo tròn khổng lồ.
//
// Cũng bỏ luôn khung height:calc(100vh - N) lồng flex: trang nằm trong wrapper flex
// của Layout.vue, chồng hai tầng flex có chiều cao cố định là nguồn của đúng những
// chỗ bị kéo giãn đó. Giờ để trang trôi tự nhiên, chỉ bảng có trần chiều cao.

.rt-shell {
  --rt-surface: #ffffff;
  --rt-sunken: #eef0f3;
  --rt-line: #e1e4e9;
  --rt-line2: #cbd0d8;
  --rt-ink: #151b23;
  --rt-soft: #5b6472;
  --rt-faint: #8a93a0;
  --rt-accent: #2c5aa6;
  --rt-accent-bg: #eaf0fa;
  /* Nút dùng xanh chủ đạo như Decision Coach; accent xanh dương giữ cho trạng thái "sắp gửi" */
  --rt-primary: #1D9E75;
  --rt-primary-dark: #0F6E56;
  --rt-primary-bg: #E1F5EE;
  --rt-ok: #1b8f63;
  --rt-ok-bg: #e7f5ee;
  --rt-warn: #c4791e;
  --rt-warn-bg: #fbf0df;
  --rt-danger: #c13a3a;
  --rt-danger-bg: #fbebeb;
  --rt-blocked: #8b2e3a;
  --rt-blocked-bg: #f6e8ea;
  --rt-mute: #6b7480;
  --rt-mute-bg: #eef0f2;

  width: 100%;
  min-width: 0;
  background: #f5f6f8;
  color: var(--rt-ink);
  font-size: 13.5px;
  line-height: 1.45;
  border: 1px solid var(--rt-line);
  border-radius: 10px;
  overflow: hidden;

  * { box-sizing: border-box; }
}

/* ---------- thanh trên ---------- */
.rt-topbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 18px;
  background: var(--rt-surface);
  border-bottom: 1px solid var(--rt-line);
}
.rt-brand {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 15px;
  white-space: nowrap;
}
.rt-brand-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--rt-accent);
  box-shadow: 0 0 0 3px var(--rt-accent-bg);
}

.rt-datenav {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--rt-sunken);
  border: 1px solid var(--rt-line);
  border-radius: 8px;
  padding: 3px;
}
.rt-iconbtn {
  width: 26px;
  height: 26px;
  line-height: 1;
  font-size: 17px;
  border: none;
  background: transparent;
  border-radius: 5px;
  cursor: pointer;
  color: var(--rt-soft);
  padding: 0;
}
.rt-iconbtn:hover { background: var(--rt-line); color: var(--rt-ink); }
.rt-datelabel { font-weight: 600; padding: 0 6px; white-space: nowrap; }

.rt-seg {
  display: flex;
  gap: 2px;
  background: var(--rt-sunken);
  border: 1px solid var(--rt-line);
  border-radius: 8px;
  padding: 3px;
}
.rt-seg button {
  border: none;
  background: transparent;
  padding: 5px 12px;
  border-radius: 6px;
  font: inherit;
  font-weight: 600;
  font-size: 13px;
  color: var(--rt-soft);
  cursor: pointer;
}
.rt-seg button.rt-seg-on { background: var(--rt-primary); color: #fff; }

.rt-topright {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.rt-clock {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--rt-soft);
  font-weight: 500;
}
.rt-pulse {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--rt-ok);
  display: inline-block;
}

.rt-btn {
  background: var(--rt-primary);
  color: #fff;
  border: none;
  padding: 8px 15px;
  border-radius: 8px;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.rt-btn:hover:not(:disabled) { background: var(--rt-primary-dark); }
.rt-btn:disabled { opacity: 0.7; cursor: default; }

/* ---------- danh sách trạng thái (panel trái) ---------- */
.rt-flabel-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.rt-clear {
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--rt-primary);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 0 6px;
}

// Danh sách dọc thay cho dải ngang: bảy nhãn nằm ngang chiếm trọn một hàng màn hình
// và vẫn tràn phải cuộn, trong khi cột trái còn thừa chỗ.
.rt-stitem {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 5px 8px;
  margin-bottom: 2px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: none;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  color: var(--rt-soft);
  text-align: left;
}
.rt-stitem:hover { background: var(--rt-sunken); }
.rt-stlabel { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rt-num {
  font-weight: 700;
  color: var(--rt-ink);
  background: var(--rt-sunken);
  padding: 0 6px;
  border-radius: 99px;
  font-size: 12px;
  flex: 0 0 auto;
}
.rt-stitem-on { font-weight: 700; }
.rt-stitem-on .rt-num { color: #fff; }

.rt-stitem-on.rt-s-will { background: var(--rt-accent-bg); color: var(--rt-accent); border-color: var(--rt-accent); }
.rt-stitem-on.rt-s-will .rt-num { background: var(--rt-accent); }
.rt-stitem-on.rt-s-wait { background: var(--rt-warn-bg); color: var(--rt-warn); border-color: var(--rt-warn); }
.rt-stitem-on.rt-s-wait .rt-num { background: var(--rt-warn); }
.rt-stitem-on.rt-s-stuck { background: var(--rt-blocked-bg); color: var(--rt-blocked); border-color: var(--rt-blocked); }
.rt-stitem-on.rt-s-stuck .rt-num { background: var(--rt-blocked); }
.rt-stitem-on.rt-s-pending { background: var(--rt-mute-bg); color: var(--rt-mute); border-color: var(--rt-mute); }
.rt-stitem-on.rt-s-pending .rt-num { background: var(--rt-mute); }
.rt-stitem-on.rt-s-skip { background: var(--rt-mute-bg); color: var(--rt-faint); border-color: var(--rt-faint); }
.rt-stitem-on.rt-s-skip .rt-num { background: var(--rt-faint); }
.rt-stitem-on.rt-s-sent { background: var(--rt-ok-bg); color: var(--rt-ok); border-color: var(--rt-ok); }
.rt-stitem-on.rt-s-sent .rt-num { background: var(--rt-ok); }
.rt-stitem-on.rt-s-failed { background: var(--rt-danger-bg); color: var(--rt-danger); border-color: var(--rt-danger); }
.rt-stitem-on.rt-s-failed .rt-num { background: var(--rt-danger); }

.rt-swatch {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex: 0 0 auto;
}


/* màu theo trạng thái — dùng chung cho chip, pill và hộp chi tiết */
.rt-s-will .rt-swatch { background: var(--rt-accent); }
.rt-s-wait .rt-swatch { background: var(--rt-warn); }
.rt-s-stuck .rt-swatch { background: var(--rt-blocked); }
.rt-s-pending .rt-swatch { background: var(--rt-mute); }
.rt-s-skip .rt-swatch { background: var(--rt-faint); }
.rt-s-sent .rt-swatch { background: var(--rt-ok); }
.rt-s-failed .rt-swatch { background: var(--rt-danger); }


/* ---------- thân ---------- */
.rt-body { display: flex; align-items: stretch; }

.rt-side {
  width: 244px;
  flex: 0 0 244px;
  background: var(--rt-surface);
  border-right: 1px solid var(--rt-line);
  padding: 16px 14px;
}
.rt-lock {
  margin-bottom: 14px;
  padding: 10px 12px;
  border: 1px solid var(--rt-accent-bg);
  border-radius: 8px;
  background: var(--rt-accent-bg);
}
.rt-lock-head {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--rt-accent);
}
.rt-lock-name {
  margin: 4px 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--rt-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rt-field { margin-bottom: 16px; }
.rt-flabel {
  display: block;
  font-size: 11.5px;
  font-weight: 700;
  color: var(--rt-soft);
  margin-bottom: 6px;
}
.rt-input {
  width: 100%;
  padding: 7px 10px;
  border-radius: 8px;
  border: 1px solid var(--rt-line);
  background: var(--rt-sunken);
  font: inherit;
  font-size: 13px;
  color: var(--rt-ink);
}
.rt-input-auto { width: auto; padding: 5px 8px; font-size: 12.5px; }
.rt-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  font-weight: 400;
  cursor: pointer;
}
.rt-check input { width: 15px; height: 15px; margin: 0; accent-color: var(--rt-accent); }

.rt-sum {
  background: var(--rt-sunken);
  border: 1px solid var(--rt-line);
  border-radius: 8px;
  padding: 11px 12px;
  margin-top: 18px;
}
.rt-sum-lbl { font-size: 12px; color: var(--rt-soft); }
.rt-sum-big { font-size: 19px; font-weight: 700; margin-bottom: 8px; }
.rt-sum-big:last-child { margin-bottom: 0; }
.rt-note { font-size: 11.5px; color: var(--rt-faint); margin: 12px 0 0; }
.rt-hint { font-size: 11px; color: var(--rt-faint); margin: 5px 0 0; }
.rt-input:disabled { opacity: 0.55; cursor: default; }
// Danh sách coach/khách có thể dài: cuộn trong menu thay vì tràn màn hình
::v-deep .rt-dd-menu { max-height: 320px; overflow-y: auto; }

/* ---------- bảng ---------- */
.rt-main { flex: 1 1 auto; min-width: 0; padding: 16px 18px; }
/* Nền trắng bo góc: nền trang là xám nên mấy nút thả trực tiếp lên đó trông như
   đang trôi lơ lửng. Khối trắng cho chúng một chỗ đứng, cùng tông với bảng ngay dưới. */
.rt-mainhead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 12px;
  padding: 10px 14px;
  background: var(--rt-surface);
  border: 1px solid var(--rt-line);
  border-radius: 8px;
}
.rt-h2 { font-size: 15px; font-weight: 700; margin: 0; }
.rt-sub { font-size: 12.5px; color: var(--rt-faint); }
.rt-sortbar {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
  color: var(--rt-soft);
}

.rt-seg-sm button { padding: 4px 10px; font-size: 12.5px; }

/* Nút phụ: viền mảnh, nền trắng. Dùng cho thao tác không đáng chiếm sự chú ý ngang
   với "Làm mới" ở thanh trên. */
.rt-ghost {
  border: 1px solid var(--rt-line2);
  background: var(--rt-surface);
  color: var(--rt-soft);
  padding: 5px 11px;
  border-radius: 7px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.rt-ghost:hover:not(:disabled) { border-color: var(--rt-primary); color: var(--rt-primary-dark); }
.rt-ghost:disabled { opacity: 0.45; cursor: default; }

.rt-divider {
  width: 1px;
  height: 20px;
  background: var(--rt-line);
}

.rt-pager {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rt-pageinfo {
  font-variant-numeric: tabular-nums;
  color: var(--rt-soft);
}

.rt-tablebox {
  background: var(--rt-surface);
  border: 1px solid var(--rt-line);
  border-radius: 8px;
  max-height: 60vh;
  overflow: auto;
}
.rt-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  min-width: 780px;
  margin: 0;
}
.rt-table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: var(--rt-faint);
  white-space: nowrap;
  padding: 9px 11px;
  border-bottom: 1px solid var(--rt-line);
  background: var(--rt-surface);
  position: sticky;
  top: 0;
  z-index: 1;
}
.rt-table td {
  padding: 8px 11px;
  border-bottom: 1px solid var(--rt-line);
  vertical-align: middle;
  height: auto;
}
.rt-table tr:last-child td { border-bottom: none; }
.rt-table tbody tr:hover { background: var(--rt-sunken); }
.rt-row-on { background: var(--rt-accent-bg); }

.rt-w-cust { width: 200px; }
/* 104px chỉ vừa "Nguyễn Th…". Tên coach tiếng Việt có dấu thường 15-20 ký tự, mà
   cột này để phân biệt coach nào phụ trách - cắt cụt thì không phân biệt được. */
.rt-w-topic { width: 150px; }
.rt-w-chan { width: 66px; }
.rt-w-time { width: 68px; }
/* Ở chế độ nhóm, cột giờ là cột đầu và mang thêm 42px thụt lề. */
.rt-table thead th.rt-w-time:first-child { width: 132px; padding-left: 42px; }
.rt-w-state { width: 112px; }
.rt-w-act { width: 168px; }

.rt-cust { display: block; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rt-code { display: block; font-size: 11px; color: var(--rt-faint); }
.rt-dim { color: var(--rt-soft); }
.rt-clip { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rt-num-cell { white-space: nowrap; font-variant-numeric: tabular-nums; }

.rt-chan {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 99px;
  white-space: nowrap;
}
.rt-chan-zalo { background: #e4eefc; color: #1d5dc2; }
.rt-chan-app { background: #e9f5ec; color: #1b7a44; }

.rt-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 99px;
  white-space: nowrap;
}
.rt-pill.rt-s-will { background: var(--rt-accent-bg); color: var(--rt-accent); }
.rt-pill.rt-s-wait { background: var(--rt-warn-bg); color: var(--rt-warn); }
.rt-pill.rt-s-stuck { background: var(--rt-blocked-bg); color: var(--rt-blocked); }
.rt-pill.rt-s-pending { background: var(--rt-mute-bg); color: var(--rt-mute); }
.rt-pill.rt-s-skip { background: var(--rt-mute-bg); color: var(--rt-faint); }
.rt-pill.rt-s-sent { background: var(--rt-ok-bg); color: var(--rt-ok); }
.rt-pill.rt-s-failed { background: var(--rt-danger-bg); color: var(--rt-danger); }

.rt-rowbtn {
  background: none;
  border: 1px solid var(--rt-line);
  border-radius: 6px;
  padding: 4px 9px;
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--rt-soft);
  cursor: pointer;
  white-space: nowrap;
}
.rt-rowbtn:hover { border-color: var(--rt-line2); color: var(--rt-ink); }
.rt-rowbtn:disabled { opacity: 0.5; cursor: default; }
.rt-rowbtn-fix { color: var(--rt-primary-dark); border-color: var(--rt-primary-bg); }
.rt-rowbtn-fix:hover { border-color: var(--rt-primary); color: var(--rt-primary-dark); }
.rt-rowbtn-del { color: var(--rt-danger); border-color: var(--rt-danger-bg); }
.rt-rowbtn-del:hover { border-color: var(--rt-danger); color: var(--rt-danger); }

.rt-rowbtn-ok { background: var(--rt-primary); color: #fff; border-color: var(--rt-primary); }
.rt-rowbtn-ok:hover:not(:disabled) { background: var(--rt-primary-dark); border-color: var(--rt-primary-dark); color: #fff; }

.rt-rtext {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--rt-line);
  border-radius: 7px;
  background: var(--rt-surface);
  color: var(--rt-ink);
  font: inherit;
  font-size: 12.5px;
  line-height: 1.5;
  resize: vertical;
}
.rt-rtext:focus {
  outline: none;
  border-color: var(--rt-accent);
}
.rt-racts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.rt-gcust > td {
  background: var(--rt-sunken);
  cursor: pointer;
  font-size: 13px;
  border-top: 1px solid var(--rt-line);
}
/* Cấp chủ đề phải tách hẳn khỏi dòng dữ liệu bên dưới: cùng nền trắng, cùng cỡ chữ
   thì mắt không phân biệt được đâu là tiêu đề nhóm, đâu là một tin. */
/* Nền lấy từ chính lớp màu của chủ đề (rt-t0..rt-t7), nên không đặt background ở đây. */
.rt-gtopic > td {
  cursor: pointer;
  padding-left: 26px;
  font-size: 12.5px;
  border-top: 1px solid var(--rt-line);
}
.rt-gtopic:hover > td { filter: brightness(0.97); }

/* Kế thừa màu chữ của lớp chủ đề trên thẻ td, khỏi khai lại tám lần. */
.rt-gtopic-name {
  font-weight: 700;
  letter-spacing: 0.2px;
}
.rt-gtopic-num {
  display: inline-block;
  min-width: 18px;
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 9px;
  /* Nền trắng mờ trên nền chủ đề, chữ giữ nguyên màu chủ đề. Đảo ngược (nền đậm,
     chữ trắng) phải dùng mẹo text-shadow vì currentColor áp cho cả hai. */
  background: rgba(255, 255, 255, 0.75);
  font-size: 10.5px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}
/* Thụt lề cho thấy dòng này thuộc nhóm nào, nhưng cột giờ chỉ rộng 68px — thụt 42px
   là chữ giờ bị đẩy sát mép phải và dính luôn vào cột Hoạt động. Nới cột và giữ khoảng
   cách phải để hai cột tách nhau. */
.rt-grow > td:first-child {
  padding-left: 42px;
  padding-right: 16px;
  white-space: nowrap;
}

.rt-caret {
  display: inline-block;
  width: 14px;
  color: var(--rt-faint);
}
.rt-topic {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 11.5px;
  font-weight: 600;
}

/* Tám cặp nền nhạt + chữ đậm CÙNG TÔNG. Chọn cặp thay vì chỉ đổi màu chữ để chip vẫn
   đọc được khi in đen trắng, và để nền không bao giờ chọi với chữ. Số lượng dừng ở 8:
   nhiều hơn thì các tông bắt đầu giống nhau và mất luôn tác dụng phân biệt. */
.rt-t0 { background: #eaf0fa; color: #2c5aa6; }
.rt-t1 { background: #e6f4ee; color: #1b7f5a; }
.rt-t2 { background: #fdf0e3; color: #a75c10; }
.rt-t3 { background: #f0ebfb; color: #6b4bb8; }
.rt-t4 { background: #fbeaf0; color: #b3365a; }
.rt-t5 { background: #e4f3f4; color: #10707a; }
.rt-t6 { background: #f8f1dc; color: #7d621b; }
.rt-t7 { background: #ecedf5; color: #4a4f7a; }

.rt-gcoach {
  margin-left: 10px;
  color: var(--rt-soft);
  font-size: 12px;
}
.rt-gcount {
  margin-left: 10px;
  color: var(--rt-faint);
  font-size: 11.5px;
}
.rt-pill-sm {
  margin-left: 6px;
  font-size: 10.5px;
  padding: 1px 6px;
}

.rt-acts { white-space: nowrap; }
.rt-acts .rt-rowbtn + .rt-rowbtn { margin-left: 4px; }

.rt-empty { padding: 44px 20px; text-align: center; color: var(--rt-faint); }
.rt-empty-t1 { font-weight: 700; color: var(--rt-soft); font-size: 14px; margin-bottom: 5px; }
.rt-empty-t2 { font-size: 12.5px; }

.rt-foot {
  display: flex;
  justify-content: space-between;
  padding: 9px 2px 0;
  font-size: 12px;
  color: var(--rt-faint);
}

/* ---------- chi tiết ---------- */
.rt-detail {
  width: 330px;
  flex: 0 0 330px;
  background: var(--rt-surface);
  border-left: 1px solid var(--rt-line);
  padding: 16px 14px;
  max-height: 80vh;
  overflow-y: auto;
}
.rt-dhead { display: flex; justify-content: space-between; gap: 8px; margin-bottom: 11px; }
.rt-dtitle { font-weight: 700; font-size: 14px; }
.rt-dsub { font-size: 11.5px; color: var(--rt-faint); }

.rt-dstate {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  font-weight: 600;
  padding: 7px 10px;
  border-radius: 8px;
  margin-bottom: 13px;
}
.rt-dstate.rt-s-will { background: var(--rt-accent-bg); color: var(--rt-accent); }
.rt-dstate.rt-s-wait { background: var(--rt-warn-bg); color: var(--rt-warn); }
.rt-dstate.rt-s-stuck { background: var(--rt-blocked-bg); color: var(--rt-blocked); }
.rt-dstate.rt-s-pending,
.rt-dstate.rt-s-skip { background: var(--rt-mute-bg); color: var(--rt-mute); }
.rt-dstate.rt-s-sent { background: var(--rt-ok-bg); color: var(--rt-ok); }
.rt-dstate.rt-s-failed { background: var(--rt-danger-bg); color: var(--rt-danger); }

.rt-facts { font-size: 12.5px; }
.rt-facts > div {
  display: flex;
  gap: 10px;
  padding: 4px 0;
  border-bottom: 1px solid var(--rt-sunken);
}
.rt-facts span { flex: 0 0 90px; color: var(--rt-faint); }
.rt-facts b { flex: 1; min-width: 0; font-weight: 500; word-break: break-word; }

.rt-dsection {
  font-weight: 700;
  font-size: 11.5px;
  color: var(--rt-soft);
  margin: 16px 0 7px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--rt-line);
}
.rt-tl { display: flex; gap: 7px; font-size: 12.5px; margin-bottom: 4px; }
.rt-tl-future { color: var(--rt-faint); }
.rt-tl-time { white-space: nowrap; color: var(--rt-soft); }

.rt-cbox { margin-bottom: 9px; }
.rt-clabel {
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--rt-faint);
  font-weight: 700;
  margin-bottom: 3px;
}
.rt-cbody {
  background: var(--rt-sunken);
  border-radius: 7px;
  padding: 8px 10px;
  font-size: 12.5px;
  white-space: pre-wrap;
  word-break: break-word;
}
.rt-dangernote {
  margin: 7px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--rt-soft);
}

.rt-jobnote {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 7px;
  background: var(--rt-danger-bg);
  color: var(--rt-danger);
  font-size: 12px;
  line-height: 1.5;
  word-break: break-word;
}

.rt-cimg {
  display: block;
  width: 100%;
  max-height: 220px;
  object-fit: contain;
  margin-top: 6px;
  border-radius: 6px;
  background: var(--rt-sunken);
}
.rt-clink {
  display: block;
  margin-top: 6px;
  font-size: 11.5px;
  color: var(--rt-accent);
  word-break: break-all;
}

.rt-steps {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.rt-step {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12.5px;
  color: var(--rt-ink);
}
.rt-step-dot {
  flex: 0 0 12px;
  text-align: center;
  font-size: 10px;
  line-height: 16px;
}
.rt-step.is-done { color: var(--rt-soft); }
.rt-step.is-done .rt-step-dot { color: var(--rt-ok); }
.rt-step.is-now,
.rt-step.is-wait { font-weight: 600; }
.rt-step.is-now .rt-step-dot,
.rt-step.is-wait .rt-step-dot { color: var(--rt-accent); }
.rt-step.is-fail { color: var(--rt-danger); font-weight: 600; }
.rt-step.is-fail .rt-step-dot { color: var(--rt-danger); }
.rt-step.is-stop,
.rt-step.is-todo { color: var(--rt-faint); }

@media (max-width: 1280px) {
  .rt-detail { width: 280px; flex-basis: 280px; }
}
@media (max-width: 980px) {
  .rt-side { display: none; }
}
</style>
